import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useHeroSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
} from '@uniqkey-frontend/shared-app';
import {
  GroupsByVaultIdOrderProperty,
} from '@uniqkey-backend-organization-web/api-client';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import ReactQueryKeyEnum from '../../../enums/ReactQueryKeyEnum';
import {
  IGetGroupsByVaultIdParams,
  TGetGroupsByVaultIdMethod,
} from '../../useEmployeeGroupsAPI/interfaces';
import {
  IOrganizationLoginGroupsTableRow,
} from '../../../components/tables/OrganizationLoginGroupsTable';
import useEmployeeGroupsAPI from '../../useEmployeeGroupsAPI';

export const REACT_QUERY_ORGANIZATION_LOGIN_GROUPS_KEY = [
  ReactQueryKeyEnum.OrganizationLoginGroups,
];

interface IPersistentFilters extends Pick<
  IGetGroupsByVaultIdParams, 'vaultId'
> {}

export interface IUseOrganizationLoginGroupsTableParams {
  persistentFilters: IPersistentFilters;
}

export interface IUseOrganizationLoginGroupsTableReturn extends
  Omit<IUseTableReturn<IOrganizationLoginGroupsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage'>,
  Omit<IUseQueryTableFetchReturn<TGetGroupsByVaultIdMethod>, 'data' | 'isFetchedAfterMount'>
{
  groups: IUseQueryTableFetchReturn<TGetGroupsByVaultIdMethod>['data'];
  selectedGroups: IUseTableReturn<IOrganizationLoginGroupsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetGroupsByVaultIdParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<
    IGetGroupsByVaultIdParams['searchQuery']>
  >;
}

interface ITablePreservationConfigs extends
  Omit<IGetGroupsByVaultIdParams,
    'page' | 'pageLength' | 'vaultId' | 'orderPropertyName' | 'isDescending'
  >,
  Pick<
    IUseTableReturn<IOrganizationLoginGroupsTableRow>,
    'columnOrderBy' | 'columnOrderDirection'
  >
{
  activePage: IGetGroupsByVaultIdParams['page'];
  perPage: IGetGroupsByVaultIdParams['pageLength'];
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: GroupsByVaultIdOrderProperty },
  ),
  columnOrderDirection: true,
};

const useOrganizationLoginGroupsTable = (
  params: IUseOrganizationLoginGroupsTableParams,
): IUseOrganizationLoginGroupsTableReturn => {
  const {
    persistentFilters,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getGroupsByVaultId } = useEmployeeGroupsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetGroupsByVaultIdParams['searchQuery']
  >(() => initialSearchQuery);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedGroups,
    resetTableMethods,
    ...restTableProps
  } = useTable<IOrganizationLoginGroupsTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: groups, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_ORGANIZATION_LOGIN_GROUPS_KEY,
    queryClient,
    request: getGroupsByVaultId,
    params: {
      page: activePage,
      pageLength: perPage,
      searchQuery,
      orderPropertyName: GroupsByVaultIdOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof GroupsByVaultIdOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      ...persistentFilters,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
  });

  return {
    groups,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedGroups,
    localization,
    searchQuery,
    setSearchQuery,
    resetActivePage,
    ...restTableProps,
  };
};

export default useOrganizationLoginGroupsTable;
